import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  CurrencySelect,
  CURRENCY_NO_CONVERSION_NOTE,
} from '@/app/components/currency-select';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { FieldError } from '@/app/components/ui/field-error';
import { useCreateVendor } from '@/hooks/use-procurement';
import { useOrgCurrency } from '@/hooks/use-settings';
import { fieldErrorMessage, parseFormErrors, type FormErrors } from '@/lib/form-errors';
import type { CreateVendorPayload, VendorStatus } from '@/lib/types';
import { vendorErrorToast } from './vendor-form';

export function VendorCreatePage() {
  const navigate = useNavigate();
  const createVendor = useCreateVendor();
  const orgCurrency = useOrgCurrency();

  const [form, setForm] = useState<CreateVendorPayload>({
    name: '',
    vendor_number: '',
    tax_id: '',
    default_payment_terms: 'Net 30',
    default_currency: '',
    status: 'active',
    preferred: false,
    address: '',
    city: '',
    state_province: '',
    country: 'US',
    postal_code: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors | null>(null);
  const errorFor = (field: string) => fieldErrorMessage(formErrors, field);

  const updateField = <K extends keyof CreateVendorPayload>(
    field: K,
    value: CreateVendorPayload[K]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors(null);
    try {
      const vendor = await createVendor.mutateAsync(form);
      toast.success('Vendor created');
      navigate(`/vendors/${vendor.id}`);
    } catch (error) {
      const errors = parseFormErrors(error, 'The vendor could not be created. Please try again.');
      setFormErrors(errors);
      toast.error(vendorErrorToast(errors, 'The vendor could not be created.'));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">Add Vendor</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Vendor Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Vendor Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
                aria-invalid={Boolean(errorFor('name'))}
              />
              <FieldError message={errorFor('name')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor_number">Vendor Number</Label>
                <Input
                  id="vendor_number"
                  value={form.vendor_number}
                  onChange={(e) => updateField('vendor_number', e.target.value)}
                  aria-invalid={Boolean(errorFor('vendor_number'))}
                />
                <FieldError message={errorFor('vendor_number')} />
              </div>
              <div>
                <Label htmlFor="tax_id">Tax ID</Label>
                <Input
                  id="tax_id"
                  value={form.tax_id}
                  onChange={(e) => updateField('tax_id', e.target.value)}
                  aria-invalid={Boolean(errorFor('tax_id'))}
                />
                <FieldError message={errorFor('tax_id')} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={form.status}
                  onChange={(e) => updateField('status', e.target.value as VendorStatus)}
                  className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                  aria-invalid={Boolean(errorFor('status'))}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <FieldError message={errorFor('status')} />
              </div>
              <div>
                <Label htmlFor="default_payment_terms">Payment Terms</Label>
                <Input
                  id="default_payment_terms"
                  value={form.default_payment_terms}
                  onChange={(e) => updateField('default_payment_terms', e.target.value)}
                  aria-invalid={Boolean(errorFor('default_payment_terms'))}
                />
                <FieldError message={errorFor('default_payment_terms')} />
              </div>
              <div>
                <Label htmlFor="default_currency">Currency</Label>
                <CurrencySelect
                  id="default_currency"
                  value={form.default_currency || orgCurrency}
                  onValueChange={(value) => updateField('default_currency', value)}
                  aria-invalid={Boolean(errorFor('default_currency'))}
                />
                <FieldError message={errorFor('default_currency')} />
                <p className="mt-1 text-xs text-gray-500">
                  {CURRENCY_NO_CONVERSION_NOTE}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="preferred"
                type="checkbox"
                checked={!!form.preferred}
                onChange={(e) => updateField('preferred', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="preferred" className="cursor-pointer">
                Preferred vendor
              </Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Primary Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={form.contact_name}
                  onChange={(e) => updateField('contact_name', e.target.value)}
                  aria-invalid={Boolean(errorFor('contact_name'))}
                />
                <FieldError message={errorFor('contact_name')} />
              </div>
              <div>
                <Label htmlFor="contact_phone">Phone</Label>
                <Input
                  id="contact_phone"
                  value={form.contact_phone}
                  onChange={(e) => updateField('contact_phone', e.target.value)}
                  aria-invalid={Boolean(errorFor('contact_phone'))}
                />
                <FieldError message={errorFor('contact_phone')} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => updateField('contact_email', e.target.value)}
                  aria-invalid={Boolean(errorFor('contact_email'))}
                />
                <FieldError message={errorFor('contact_email')} />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={form.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  aria-invalid={Boolean(errorFor('website'))}
                />
                <FieldError message={errorFor('website')} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Address</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                value={form.address}
                onChange={(e) => updateField('address', e.target.value)}
                aria-invalid={Boolean(errorFor('address'))}
              />
              <FieldError message={errorFor('address')} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  aria-invalid={Boolean(errorFor('city'))}
                />
                <FieldError message={errorFor('city')} />
              </div>
              <div>
                <Label htmlFor="state_province">State</Label>
                <Input
                  id="state_province"
                  value={form.state_province}
                  onChange={(e) => updateField('state_province', e.target.value)}
                  aria-invalid={Boolean(errorFor('state_province'))}
                />
                <FieldError message={errorFor('state_province')} />
              </div>
              <div>
                <Label htmlFor="postal_code">Zip Code</Label>
                <Input
                  id="postal_code"
                  value={form.postal_code}
                  onChange={(e) => updateField('postal_code', e.target.value)}
                  aria-invalid={Boolean(errorFor('postal_code'))}
                />
                <FieldError message={errorFor('postal_code')} />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={form.country}
                  onChange={(e) => updateField('country', e.target.value)}
                  aria-invalid={Boolean(errorFor('country'))}
                />
                <FieldError message={errorFor('country')} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              id="notes"
              value={form.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={3}
              aria-invalid={Boolean(errorFor('notes'))}
            />
            <FieldError message={errorFor('notes')} />
          </CardContent>
        </Card>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={createVendor.isPending}>
            {createVendor.isPending ? 'Creating...' : 'Create Vendor'}
          </Button>
        </div>
      </form>
    </div>
  );
}
